import { useState, useEffect, useCallback } from 'react';
import { learnerService } from '../services/learnerService';

export interface NextSkillRecommendation {
  skillId: string;
  code: string;
  name: string;
  difficulty: 'easy' | 'medium' | 'hard';
  reason: string;
  prerequisitesMet: Array<{ skillId: string; name: string; correctCount: number; evidenceCount: number }>;
}

export interface SkillNeedingCheck {
  skillId: string;
  code: string;
  name: string;
  blockedBy: Array<{ skillId: string; code: string | null; name: string }>;
}

export interface UseNextSkillsReturn {
  recommendations: NextSkillRecommendation[];
  // Skills that aren't recommendable yet purely because a prerequisite has
  // never been assessed (as opposed to genuinely below mastery) - see
  // recommendationService.js's getSkillsNeedingCheck. Distinct from
  // `recommendations` so the UI can offer "quick check" rather than staying
  // silently stuck.
  needsCheck: SkillNeedingCheck[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches "what's next" for a learner (GET /api/learners/:learnerId/next,
 * Phase 2's recommendationService - a deterministic DB query, never an LLM
 * call, per IMPLEMENTATION_PLAN.md §5's deterministic/generative boundary).
 */
export const useNextSkills = (
  learnerId: string | null,
  options?: { subject?: string; limit?: number }
): UseNextSkillsReturn => {
  const [recommendations, setRecommendations] = useState<NextSkillRecommendation[]>([]);
  const [needsCheck, setNeedsCheck] = useState<SkillNeedingCheck[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNext = useCallback(async () => {
    if (!learnerId) {
      setRecommendations([]);
      setNeedsCheck([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const data = await learnerService.getNextSkills(learnerId, token, options);
      setRecommendations(data.recommendations || []);
      setNeedsCheck(data.needsCheck || []);
    } catch (err) {
      console.error('Error fetching next skills:', err);
      setError('Failed to load recommendations');
      setRecommendations([]);
      setNeedsCheck([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnerId, options?.subject, options?.limit]);

  useEffect(() => {
    fetchNext();
  }, [fetchNext]);

  return { recommendations, needsCheck, loading, error, refetch: fetchNext };
};
